import { useCallback, useEffect, useRef } from 'react';
import { AuthProvider } from '@40seas/entities/auth';
import { UnauthorizedException } from '@40seas/errors';
import { AuthUser, AuthError } from '../types';
import { authClient } from '../AuthClient';
import { getAuthProvider } from '../getAuthProvider';
import { authStorage } from '../storage';

interface UseAuthInitializationOptions {
    onRedirectCallback?: (appState?: { returnTo?: string }) => void;
    onAuthStateChange: (authenticated: boolean, user: AuthUser | null) => void;
    onLoadingChange: (loading: boolean) => void;
    onError?: (error: AuthError) => void;
}

/**
 * Parses OAuth parameters from the current URL.
 */
function parseOAuthParams(): {
    code: string | null;
    state: string | null;
    provider: string | null;
    error: string | null;
    errorDescription: string | null;
} {
    const urlParams = new URLSearchParams(window.location.search);
    return {
        code: urlParams.get('code'),
        state: urlParams.get('state'),
        provider: urlParams.get('provider'),
        error: urlParams.get('error'),
        errorDescription: urlParams.get('error_description'),
    };
}

/**
 * Cleans OAuth-related parameters from the URL.
 */
export function cleanOAuthParams(): void {
    const url = new URL(window.location.href);
    // Success parameters
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('provider');
    url.searchParams.delete('scope');
    url.searchParams.delete('authuser');
    url.searchParams.delete('prompt');
    // Error parameters (RFC 6749 Section 4.1.2.1)
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    url.searchParams.delete('error_uri');
    window.history.replaceState({}, '', url.toString());
}

/**
 * Hook to handle authentication initialization.
 * Manages OAuth callback processing, URL parameter parsing, and initial auth check.
 */
export function useAuthInitialization({ onRedirectCallback, onAuthStateChange, onLoadingChange, onError }: UseAuthInitializationOptions) {
    // Store callbacks in refs to prevent re-initialization when parent re-renders
    // with new callback references. Refs are updated on each render but don't
    // cause useCallback dependencies to change.
    const onRedirectCallbackRef = useRef(onRedirectCallback);
    const onErrorRef = useRef(onError);
    onRedirectCallbackRef.current = onRedirectCallback;
    onErrorRef.current = onError;

    const handleOAuthCallback = useCallback(
        async (code: string, state: string | null, provider: string | null) => {
            try {
                let response;
                if (provider === 'microsoft') {
                    response = await authClient.handleMicrosoftCallback(code, state || undefined);
                } else {
                    // Default to Google if no provider specified
                    response = await authClient.handleGoogleCallback(code, state || undefined);
                }
                onAuthStateChange(true, response.user);

                // Handle redirect callback - check session storage first, then decode from state
                const appState = sessionStorage.getItem('auth_app_state');
                if (appState) {
                    sessionStorage.removeItem('auth_app_state');
                    if (onRedirectCallbackRef.current) {
                        try {
                            onRedirectCallbackRef.current(JSON.parse(appState));
                        } catch (parseError) {
                            console.warn('Failed to parse auth_app_state from sessionStorage:', parseError);
                        }
                    }
                } else if (state && onRedirectCallbackRef.current) {
                    // Try to decode returnTo from state (base64 encoded by backend)
                    try {
                        const stateData = JSON.parse(atob(state));
                        if (stateData.returnTo) {
                            onRedirectCallbackRef.current({ returnTo: stateData.returnTo });
                        }
                    } catch (parseError) {
                        console.warn('Failed to decode returnTo from OAuth state parameter:', parseError);
                    }
                }
            } catch (error) {
                throw new UnauthorizedException({
                    message: `OAuth callback failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    code: 'OAUTH_CALLBACK_FAILED',
                });
            }
        },
        [onAuthStateChange],
    );

    const initializeAuth = useCallback(async () => {
        onLoadingChange(true);
        try {
            // Skip OAuth code processing on /auth/callback route (popup OAuth callbacks)
            const isOAuthCallbackRoute = window.location.pathname === '/auth/callback';
            if (isOAuthCallbackRoute) {
                const authenticated = authClient.isAuthenticated();
                const user = authClient.getUser();
                if (authenticated && user) {
                    onAuthStateChange(true, user);
                } else {
                    onAuthStateChange(false, null);
                }
                return;
            }

            // Check for OAuth callback
            const { code, state, provider, error, errorDescription } = parseOAuthParams();

            // Handle OAuth error response
            if (error) {
                cleanOAuthParams();
                sessionStorage.removeItem('auth_app_state');
                if (onErrorRef.current) {
                    const authError: AuthError = {
                        message: errorDescription || error,
                        code: `OAUTH_ERROR_${error.toUpperCase()}`,
                    };
                    onErrorRef.current(authError);
                }
                onAuthStateChange(false, null);
                return;
            }

            if (code) {
                try {
                    await handleOAuthCallback(code, state, provider);
                    cleanOAuthParams();
                    return;
                } catch (error) {
                    cleanOAuthParams();
                    sessionStorage.removeItem('auth_app_state');
                    if (onErrorRef.current) {
                        const authError: AuthError = {
                            message: error instanceof Error ? error.message : 'OAuth authentication failed',
                            code: (error as { code?: string })?.code || 'OAUTH_CALLBACK_FAILED',
                        };
                        onErrorRef.current(authError);
                    }
                    onAuthStateChange(false, null);
                    return;
                }
            }

            // Check if auth data exists (for page refreshes)
            const refreshToken = authStorage.getRefreshToken();
            const user = authClient.getUser();

            const isSelfManaged = getAuthProvider() === AuthProvider.SelfManaged;
            const tokenExpired = authClient.isTokenExpired();
            const shouldAttemptRefresh = (!isSelfManaged && Boolean(refreshToken || user)) || (isSelfManaged && tokenExpired);

            if (shouldAttemptRefresh) {
                // Attempt refresh when:
                // - Auth0 has local auth state (refresh token or user), OR
                // - Self-managed token is expired and needs renewal
                try {
                    await authClient.refreshToken();
                    const refreshedUser = authClient.getUser();
                    if (refreshedUser) {
                        onAuthStateChange(true, refreshedUser);
                    } else {
                        onAuthStateChange(false, null);
                    }
                } catch {
                    onAuthStateChange(false, null);
                }
            } else if (isSelfManaged && !tokenExpired) {
                const existingUser = authClient.getUser();
                if (existingUser) {
                    onAuthStateChange(true, existingUser);
                } else {
                    onAuthStateChange(false, null);
                }
            } else {
                onAuthStateChange(false, null);
            }
        } catch {
            onAuthStateChange(false, null);
        } finally {
            onLoadingChange(false);
        }
    }, [handleOAuthCallback, onAuthStateChange, onLoadingChange]);

    // Initialize auth on mount
    useEffect(() => {
        void initializeAuth();
    }, [initializeAuth]);

    return { initializeAuth };
}

/**
 * Hook to listen for token changes from other parts of the app (e.g., popup OAuth flow).
 */
export function useAuthTokenListener(onTokensChanged: () => void) {
    useEffect(() => {
        const handleTokensChanged = () => {
            onTokensChanged();
        };

        window.addEventListener('auth-tokens-changed', handleTokensChanged);
        return () => {
            window.removeEventListener('auth-tokens-changed', handleTokensChanged);
        };
    }, [onTokensChanged]);
}
